import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { useAppSelector } from "../../../redux/store";
import { apiBlob, apiGet, apiPost } from "../../../services/api/apiClient";
import {
  Button,
  Card,
  Empty,
  ErrorState,
  Grid,
  Loading,
  PageHeader,
  SearchInput,
  Section,
  Tag,
} from "../../../ui";

type SectionSummary = {
  key: string;
  label: string;
  count: number;
};

type LibraryItem = {
  id: string;
  displayName: string;
  kind: string;
  docType?: string;
  groupKey?: string;
  groupLabel?: string;
  date: string;
  size?: number;
  tags?: string[];
  isCurrent?: boolean;
  extension?: string;
};

type SectionPage = {
  section: SectionSummary;
  items: LibraryItem[];
  nextOffset: number | null;
  total: number;
  types?: string[];
};

const docFilterStyle = {
  height: 36,
  border: "1px solid var(--tpc-300)",
  borderRadius: 0,
  padding: "0 8px",
  fontFamily: '"Brandon Grotesque", sans-serif',
  fontSize: 12,
  background: "#ffffff",
};

const PAGE_SIZE = 25;

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif", "heic", "heif", "tif", "tiff"];

const isImageItem = (item: { extension?: string }) =>
  IMAGE_EXTENSIONS.includes((item.extension || "").toLowerCase());

const previewArrowStyle: CSSProperties = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: 44,
  height: 44,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  paddingBottom: 4,
  border: "1px solid var(--tpc-300, #c9c9c9)",
  background: "rgba(255, 255, 255, 0.94)",
  color: "var(--tpc-900, #1c1c1c)",
  fontSize: 26,
  lineHeight: 1,
  zIndex: 2,
};

const formatDate = (value: string) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      });
};

const formatSize = (value?: number) => {
  if (!value) return "";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const docTypeLabel = (value?: string) => {
  switch (value) {
    case "draw":
      return "Draw";
    case "budget":
      return "Budget";
    case "invoice":
      return "Collective invoice";
    case "product-summary":
      return "Product Summary";
    case "project-estimate":
      return "Project Estimate";
    case "sd-invoice":
      return "SD Invoice";
    case "deposit":
      return "Deposit";
    case "retainer":
      return "Retainer";
    default:
      return "";
  }
};

export default function DocumentLibrary() {
  const [searchParams] = useSearchParams();
  const sectionKey = searchParams.get("section") || "";
  const focusId = searchParams.get("item") || "";
  const { projects, selectedProject } = useAppSelector((state) => state.projects);
  const project = selectedProject || projects?.[0];
  const projectId = project?.id;

  const [sections, setSections] = useState<SectionSummary[]>([]);
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [sectionsError, setSectionsError] = useState<string | null>(null);

  const [page, setPage] = useState<SectionPage | null>(null);
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [docType, setDocType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [openingID, setOpeningID] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    item: LibraryItem;
    url: string;
    previous: LibraryItem | null;
    next: LibraryItem | null;
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadNote, setUploadNote] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const sentinel = useRef<HTMLDivElement | null>(null);
  const uploadInput = useRef<HTMLInputElement | null>(null);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !projectId) return;
    setUploading(true);
    setUploadNote(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const data: { fileName?: string } = await apiPost({
        endpoint: "/document-library/upload",
        params: { projectId },
        data: form,
        withAuth: true,
      });
      setUploadNote({
        kind: "success",
        text: `${data?.fileName || file.name} was sent to your project team.`,
      });
    } catch (error: any) {
      setUploadNote({
        kind: "error",
        text: error?.message || "Could not upload this file.",
      });
    } finally {
      setUploading(false);
    }
  };

  const uploadControl = (
    <>
      <input
        ref={uploadInput}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.heic,.heif,.tif,.tiff,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.rtf,.dwg,.dxf"
        style={{ display: "none" }}
        onChange={handleUpload}
      />
      <Button
        size="sm"
        onClick={() => uploadInput.current?.click()}
        disabled={uploading || !projectId}
      >
        {uploading ? "Uploading…" : "Upload to your project"}
      </Button>
    </>
  );

  const uploadMessage = uploadNote && (
    <p
      className={`ui-callout ui-callout--${uploadNote.kind === "error" ? "error" : "success"} ui-mb-16`}
    >
      {uploadNote.text}
    </p>
  );

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!projectId) {
      setSectionsLoading(false);
      return;
    }
    let cancelled = false;
    setSectionsLoading(true);
    setSectionsError(null);
    apiGet({
      endpoint: "/document-library/sections",
      params: { projectId },
      withAuth: true,
    })
      .then((data: { sections?: SectionSummary[] }) => {
        if (!cancelled) setSections(data.sections || []);
      })
      .catch((error: any) => {
        if (!cancelled) setSectionsError(error?.message || "Could not load the document library.");
      })
      .finally(() => {
        if (!cancelled) setSectionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const loadSection = useCallback(
    async (offset: number, append: boolean) => {
      if (!projectId || !sectionKey) return;
      setItemsLoading(true);
      setItemsError(null);
      try {
        const data: SectionPage = await apiGet({
          endpoint: "/document-library/section",
          params: {
            projectId,
            section: sectionKey,
            offset,
            limit: PAGE_SIZE,
            ...(debouncedQuery ? { q: debouncedQuery } : {}),
            ...(docType ? { type: docType } : {}),
            ...(fromDate ? { from: fromDate } : {}),
            ...(toDate ? { to: toDate } : {}),
          },
          withAuth: true,
        });
        setPage(data);
        setItems((current) => (append ? [...current, ...(data.items || [])] : data.items || []));
      } catch (error: any) {
        setItemsError(error?.message || "Could not load this section.");
      } finally {
        setItemsLoading(false);
      }
    },
    [projectId, sectionKey, debouncedQuery, docType, fromDate, toDate],
  );

  useEffect(() => {
    if (!sectionKey) {
      setItems([]);
      setPage(null);
      return;
    }
    setItems([]);
    loadSection(0, false);
  }, [sectionKey, loadSection]);

  // A type or date range belongs to one section; do not carry it into the
  // next section where it may match nothing.
  useEffect(() => {
    setDocType("");
    setFromDate("");
    setToDate("");
  }, [sectionKey]);

  useEffect(() => {
    if (!sectionKey || !page || page.nextOffset == null || itemsLoading) return;
    const node = sentinel.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadSection(page.nextOffset as number, true);
        }
      },
      { rootMargin: "240px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [sectionKey, page, itemsLoading, loadSection]);

  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  // An invoice's "View draw" link names the file; bring it into view instead
  // of leaving the client to find it among the month's documents.
  useEffect(() => {
    if (!focusId) return;
    const row = document.getElementById(`doc-row-${focusId}`);
    if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusId, items]);

  const previewable = (item: LibraryItem) =>
    item.extension === "pdf" || isImageItem(item);

  const openItem = async (item: LibraryItem, mode: "preview" | "download") => {
    setOpeningID(item.id);
    setOpenError(null);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 30000);
    try {
      if (mode === "preview" && previewable(item)) {
        // Dropbox's temporary link is an attachment, so fetch the bytes
        // through the API and let the browser render them inline.
        const [blob, neighbors] = await Promise.all([
          apiBlob({
            endpoint: "/document-library/item/content",
            params: { id: item.id },
            withAuth: true,
            signal: controller.signal,
          }),
          isImageItem(item)
            ? apiGet({
                endpoint: "/document-library/item/neighbors",
                params: { id: item.id },
                withAuth: true,
                signal: controller.signal,
              })
            : Promise.resolve({ previous: null, next: null }),
        ]);
        setPreview({
          item,
          url: URL.createObjectURL(blob),
          previous: neighbors?.previous || null,
          next: neighbors?.next || null,
        });
      } else {
        const data: { url: string } = await apiGet({
          endpoint: "/document-library/item/link",
          params: { id: item.id },
          withAuth: true,
          signal: controller.signal,
        });
        window.open(data.url, "_blank", "noopener,noreferrer");
      }
    } catch (error: any) {
      setOpenError(
        error?.name === "AbortError"
          ? "This file is taking too long to open. Try again in a moment."
          : error?.message || "Could not open this file.",
      );
    } finally {
      window.clearTimeout(timeout);
      setOpeningID(null);
    }
  };

  const showNeighbor = useCallback(
    async (direction: "previous" | "next") => {
      if (!preview || previewLoading) return;
      const target = preview[direction];
      if (!target) return;
      setPreviewLoading(true);
      setOpenError(null);
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 30000);
      try {
        const [blob, neighbors] = await Promise.all([
          apiBlob({
            endpoint: "/document-library/item/content",
            params: { id: target.id },
            withAuth: true,
            signal: controller.signal,
          }),
          apiGet({
            endpoint: "/document-library/item/neighbors",
            params: { id: target.id },
            withAuth: true,
            signal: controller.signal,
          }),
        ]);
        setPreview({
          item: target,
          url: URL.createObjectURL(blob),
          previous: neighbors?.previous || null,
          next: neighbors?.next || null,
        });
      } catch (error: any) {
        setOpenError(
          error?.name === "AbortError"
            ? "This photo is taking too long to open. Try again in a moment."
            : error?.message || "Could not open this photo.",
        );
      } finally {
        window.clearTimeout(timeout);
        setPreviewLoading(false);
      }
    },
    [preview, previewLoading],
  );

  useEffect(() => {
    if (!preview) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPreview(null);
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        void showNeighbor("previous");
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        void showNeighbor("next");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [preview, showNeighbor]);

  const groupItems = useMemo(() => {
    const groups: Array<{ key: string; label: string; items: LibraryItem[] }> = [];
    const index = new Map<string, number>();
    items.forEach((item) => {
      const key = item.groupKey || "__flat";
      if (!index.has(key)) {
        index.set(key, groups.length);
        groups.push({ key, label: item.groupLabel || "", items: [] });
      }
      groups[index.get(key) as number].items.push(item);
    });
    return groups;
  }, [items]);

  if (!projectId) {
    return (
      <>
        <PageHeader
          eyebrow="Documents"
          title="Documents"
          summary="The project library your team shares with you."
        />
        <Empty title="No project selected">Choose a project to open its document library.</Empty>
      </>
    );
  }

  if (sectionKey) {
    const label = page?.section.label || sections.find((item) => item.key === sectionKey)?.label || "Section";
    return (
      <>
        <PageHeader
          crumbs={[
            { label: "Documents", to: "/project-home/documents" },
            { label },
          ]}
          title={label}
          summary={`${page?.total ?? 0} ${(page?.total ?? 0) === 1 ? "document" : "documents"}`}
          actions={
            <>
              <Link to="/project-home/documents" className="ui-btn ui-btn--secondary ui-btn--sm">
                All sections
              </Link>
              {uploadControl}
            </>
          }
        />
        {uploadMessage}
        <div className="ui-toolbar">
          <SearchInput
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Search ${label.toLowerCase()}`}
            aria-label={`Search ${label}`}
          />
          <select
            aria-label="Document type"
            value={docType}
            onChange={(event) => setDocType(event.target.value)}
            style={docFilterStyle}
          >
            <option value="">All types</option>
            {(page?.types || []).map((value) => (
              <option key={value} value={value}>
                {docTypeLabel(value) || value}
              </option>
            ))}
          </select>
          <input
            type="date"
            aria-label="From date"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            style={docFilterStyle}
          />
          <input
            type="date"
            aria-label="To date"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            style={docFilterStyle}
          />
          {(query || docType || fromDate || toDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setQuery("");
                setDocType("");
                setFromDate("");
                setToDate("");
              }}
            >
              Clear
            </Button>
          )}
        </div>

        {itemsError && <ErrorState what="this section" message={itemsError} />}
        {openError && (
          <p className="ui-callout ui-callout--error ui-mb-16">{openError}</p>
        )}

        {items.length === 0 && itemsLoading && <Loading>Loading documents…</Loading>}
        {items.length === 0 && !itemsLoading && !itemsError && (
          <Empty title="Nothing here yet">
            {debouncedQuery || docType || fromDate || toDate
              ? "No documents match these filters."
              : "Documents will appear here when the team adds them."}
          </Empty>
        )}

        <div className="ui-stack">
          {groupItems.map((group) => (
            <section key={group.key} className="ui-section">
              {group.label && <h2 className="ui-section-label">{group.label}</h2>}
              <div className="ui-table-wrap">
                <table className="ui-table">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Type</th>
                      <th>Date</th>
                      <th className="ui-right">Size</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((item) => (
                      <tr
                        key={item.id}
                        id={`doc-row-${item.id}`}
                        className={item.id === focusId ? "ui-table__row--focus" : undefined}
                      >
                        <td>{item.displayName}</td>
                        <td>
                          {docTypeLabel(item.docType) ? (
                            <Tag>{docTypeLabel(item.docType)}</Tag>
                          ) : item.tags?.length ? (
                            <span className="ui-row" style={{ gap: 4 }}>
                              {item.tags.map((tag) => (
                                <Tag key={tag}>{tag}</Tag>
                              ))}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="ui-nowrap">{formatDate(item.date)}</td>
                        <td className="ui-right ui-nowrap">{formatSize(item.size)}</td>
                        <td className="ui-right">
                          <span className="ui-row" style={{ justifyContent: "flex-end", gap: 4 }}>
                            {previewable(item) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openItem(item, "preview")}
                                disabled={openingID === item.id}
                              >
                                {openingID === item.id ? "Opening…" : "Preview"}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openItem(item, "download")}
                              disabled={openingID === item.id}
                            >
                              Download
                            </Button>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
        <div ref={sentinel} />
        {itemsLoading && items.length > 0 && <Loading>Loading more…</Loading>}

        {preview && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={preview.item.displayName}
            onClick={() => setPreview(null)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 80,
              background: "rgba(19, 21, 23, 0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
            }}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                display: "flex",
                flexDirection: "column",
                width: "min(1100px, 100%)",
                height: "min(86vh, 900px)",
                background: "#fff",
                border: "1px solid var(--tpc-200)",
              }}
            >
              <div
                className="ui-row"
                style={{
                  justifyContent: "space-between",
                  padding: "12px 16px",
                  borderBottom: "1px solid var(--tpc-200)",
                }}
              >
                <strong>{preview.item.displayName}</strong>
                <span className="ui-row" style={{ gap: 8 }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => openItem(preview.item, "download")}
                    disabled={openingID === preview.item.id}
                  >
                    {openingID === preview.item.id ? "Opening…" : "Download"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setPreview(null)}>
                    Close
                  </Button>
                </span>
              </div>
              <div style={{ flex: 1, minHeight: 0, background: "var(--tpc-50)", position: "relative" }}>
                {preview.item.extension === "pdf" ? (
                  <iframe
                    title={preview.item.displayName}
                    src={preview.url}
                    style={{ width: "100%", height: "100%", border: 0 }}
                  />
                ) : (
                  <img
                    src={preview.url}
                    alt={preview.item.displayName}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                )}
                {isImageItem(preview.item) && (
                  <>
                    {preview.previous && (
                      <button
                        type="button"
                        aria-label="Previous photo"
                        title="Previous photo (←)"
                        disabled={previewLoading}
                        onClick={() => showNeighbor("previous")}
                        style={{
                          ...previewArrowStyle,
                          left: 16,
                          cursor: previewLoading ? "default" : "pointer",
                        }}
                      >
                        ‹
                      </button>
                    )}
                    {preview.next && (
                      <button
                        type="button"
                        aria-label="Next photo"
                        title="Next photo (→)"
                        disabled={previewLoading}
                        onClick={() => showNeighbor("next")}
                        style={{
                          ...previewArrowStyle,
                          right: 16,
                          cursor: previewLoading ? "default" : "pointer",
                        }}
                      >
                        ›
                      </button>
                    )}
                  </>
                )}
                {previewLoading && (
                  <span
                    style={{
                      position: "absolute",
                      bottom: 12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      border: "1px solid var(--tpc-200)",
                      background: "rgba(255, 255, 255, 0.94)",
                      padding: "4px 10px",
                      fontSize: 12,
                    }}
                  >
                    Loading…
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Project library"
        title="Documents"
        summary="The project library your team shares with you, organised the same way the team keeps it."
        actions={uploadControl}
      />
      {uploadMessage}
      {sectionsError && <ErrorState what="the document library" message={sectionsError} />}
      {sectionsLoading && <Loading>Loading sections…</Loading>}
      {!sectionsLoading && !sectionsError && sections.length === 0 && (
        <Empty title="No documents yet">
          Your project documentation library will appear here once files are synced.
        </Empty>
      )}
      {!sectionsLoading && sections.length > 0 && (
        <Section title="Sections">
          <Grid cols={3}>
            {sections.map((section) => (
              <Card
                key={section.key}
                to={`/project-home/documents?section=${encodeURIComponent(section.key)}`}
                title={section.label}
                footer={`${section.count} ${section.count === 1 ? "document" : "documents"}`}
              >
                Open this section of the project library.
              </Card>
            ))}
          </Grid>
        </Section>
      )}
      {selectedProject?.name && (
        <p className="ui-caption ui-mt-24">{selectedProject.name}</p>
      )}
    </>
  );
}
